import { useState, useRef, useMemo, useCallback } from 'react';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import { MarketsTable } from '@/features/markets/components/markets-table';
import { cn } from '@/utils/cn';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { imgFromAbbr, abbrFromSym } from '@/utils/token-symbol';
import { FavoriteButton } from '@/features/markets/components/favorite-button';
import { getMaxLeverage } from '../order/utils/math';
import { MarketStatus, useMarketStore } from '@/store/use-markets-store';
import { formatNumber } from '@/utils/value-format';
import { useStore } from 'zustand';
import TooltipWrapper from '@/components/ui/tooltip';
import { SYMBOL_CATEGORIES } from '@/app/pages/markets';
import { Info } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export const MarketsDropdown = () => {
  const { symbol } = useParams() as { symbol: string };
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { marketSpec } = useStore(useMarketStore(symbol), (state) => ({
    marketSpec: state.marketSpec,
  }));

  const maxLeverage: string | undefined = useMemo(() => {
    return getMaxLeverage(marketSpec)?.decimal;
  }, [marketSpec]);

  const handleRowClick = useCallback((symbol: string) => {
    navigate(`/trade/${symbol}`);
    setIsOpen(false);
  }, []);

  return (
    <div className="min-w-max border-r border-border" ref={dropdownRef}>
      <div className="flex h-full gap-4 bg-background pl-6 transition-colors hover:bg-vestgrey-800 sm:pl-8">
        <div className="hidden self-center md:block">
          <FavoriteButton symbol={symbol} size={18} />
        </div>
        <div className="grid grow">
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <button className="flex items-center justify-between gap-3 pr-6 sm:gap-12 sm:pr-8">
                <div className="flex min-w-max items-center gap-3">
                  <img
                    src={imgFromAbbr(abbrFromSym(symbol))}
                    alt="Token Image"
                    className="h-6 w-6"
                  />
                  <div className="flex flex-col">
                    <span
                      className={cn(
                        'text-font font-bold',
                        marketSpec.status === MarketStatus.TRADING && 'text-lg',
                      )}
                    >
                      {abbrFromSym(symbol)}
                      <span className="hidden sm:inline"> / USDC</span>
                    </span>
                    {marketSpec.status === MarketStatus.HALT && (
                      <div className="max-w-max rounded-full bg-primary px-2 py-0.5 font-mono text-xs font-medium uppercase text-black">
                        Closed
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {maxLeverage && (
                      <div className="hidden items-center justify-center rounded bg-[#271714] px-2 py-1.5 sm:flex">
                        <span className="font-mono leading-none text-primary">
                          {formatNumber(maxLeverage)}X
                        </span>
                      </div>
                    )}
                    {marketSpec.isolated && (
                      <TooltipWrapper
                        content={
                          <span>
                            Backed by isolated liquidity pool. See{' '}
                            <Link
                              className="underline"
                              target="_blank"
                              rel="noreferrer"
                              to="https://docs.vest.exchange/overview/traders/perpetual-contract-specifications"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Docs
                            </Link>{' '}
                            for details.
                          </span>
                        }
                      >
                        <div className="flex items-center justify-center rounded bg-[#271714] px-1 max-sm:h-5 sm:px-2 sm:py-1.5">
                          <span className="font-mono uppercase leading-none text-primary max-sm:text-xs sm:mr-px">
                            ISO
                          </span>
                        </div>
                      </TooltipWrapper>
                    )}
                  </div>
                  {SYMBOL_CATEGORIES['Dominance'].includes(
                    abbrFromSym(symbol),
                  ) && (
                    <TooltipWrapper
                      content={
                        <span>
                          Price represesnts the market cap of relevant symbols
                          over total market cap (in %). See{' '}
                          <Link
                            className="underline"
                            target="_blank"
                            rel="noreferrer"
                            to="https://docs.vest.exchange/overview/traders/price-indices#market-cap-dominance-prices"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Docs
                          </Link>{' '}
                          for the full methodology.
                        </span>
                      }
                    >
                      <Info className="text-primary" size={14} />
                    </TooltipWrapper>
                  )}
                  {SYMBOL_CATEGORIES['Index'].includes(abbrFromSym(symbol)) && (
                    <TooltipWrapper
                      content={
                        <span>
                          Price represesnts the normalized market cap of
                          relevant symbols. See{' '}
                          <Link
                            className="underline"
                            target="_blank"
                            rel="noreferrer"
                            to="https://docs.vest.exchange/overview/traders/price-indices#market-cap-dominance-prices"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Docs
                          </Link>{' '}
                          for the full methodology.
                        </span>
                      }
                    >
                      <Info className="text-primary" size={14} />
                    </TooltipWrapper>
                  )}
                </div>
                <div>
                  <ChevronDownIcon
                    strokeWidth={1.5}
                    className={cn(
                      'text-vestgrey-100 transition-transform duration-300',
                      {
                        'rotate-180': isOpen,
                      },
                    )}
                  />
                </div>
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-screen max-w-8xl border-0 p-0"
              sideOffset={0}
            >
              <MarketsTable onRowClick={handleRowClick} />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
};
